import axios from 'axios';
import { setAuthorizationHeaders } from '@/api/utils';
import AuthService from '@/services/authService.ts';
import { obtainPersonalAccessToken } from '@/api/deepsign/personalAccessApi.ts';
import { useDocumentAuth } from '@/composables/useDocumentAuth.ts';
import { useStorage } from '@vueuse/core';

const deepSignApi = axios.create({
  baseURL: `${import.meta.env.VITE_DEEPSIGN_API_BASE_URL}/v1/`
});

deepSignApi.interceptors.request.use(
  async (config) => {
    const headers = {};

    await setAuthorizationHeaders(headers);

    Object.assign(config.headers, headers);
    return {
      ...config
    };
  },
  (error) => {
    return Promise.reject(error);
  }
);

deepSignApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const personalAccessConfig = useStorage(
      'personalAccessConfig',
      '',
      sessionStorage
    );
    const originalRequest = error.config;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const sessionPersonalAccessConfig = JSON.parse(
      personalAccessConfig.value || '{}'
    );
    const sid = urlParams.get('sid');
    const isSidMatch = isSidMatching(sessionPersonalAccessConfig, sid);

    if (
      !originalRequest._retry &&
      !AuthService.isAuthenticated &&
      sessionPersonalAccessConfig.signKey &&
      isSidMatch
    ) {
      const documentAuth = useDocumentAuth();
      originalRequest._retry = true;
      try {
        const tokenResponse = await obtainPersonalAccessToken(
          sessionPersonalAccessConfig.signKey,
          {
            headers: {
              Authorization: `SID ${sid}`
            }
          }
        );

        if (tokenResponse) {
          documentAuth.login(
            tokenResponse.data.documentId,
            tokenResponse.data.token
          );
          originalRequest.headers.Authorization = `Bearer ${tokenResponse.data.token}`;
          //recall Api with new token
          return deepSignApi(originalRequest);
        }
      } catch (error) {
        console.error('Error obtaining personal access token:', error);
      }
    }
    return Promise.reject(error);
  }
);

function isSidMatching(
  sessionPersonalAccessConfig: {
    signKey: string;
    sid: string | null;
    documentId: string;
  } | null,
  sid: string | null
): boolean {
  if (!sessionPersonalAccessConfig || !sid) {
    return false;
  }

  return sessionPersonalAccessConfig.sid === sid;
}

export default deepSignApi;
